import React from 'react';

const ObisSpinner: React.FC<{ text?: string }> = ({ text = 'Loading...' }) => {
  return (
    <div className="flex flex-col items-center justify-center space-y-10 p-8">
      <div className="relative w-40 h-40 flex items-center justify-center">
        {/* Core Node: The Central OBIS App (Bluish Gradient Logo) */}
        <div className="absolute flex items-center justify-center z-10">
          {/* Outer pinging effect */}
          <div className="w-14 h-14 bg-gradient-to-br from-[#2AC3A2] to-[#2C71E5] rounded-[14px] animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite] opacity-40"></div>
          {/* Central Logo Box */}
          <div className="absolute w-14 h-14 bg-gradient-to-br from-[#2AC3A2] to-[#2C71E5] rounded-[14px] shadow-[0_0_25px_rgba(44,113,229,0.6)] flex items-center justify-center">
            <span className="text-white font-black text-xl tracking-tighter ml-0.5">OB</span>
          </div>
        </div>

        {/* The Orbit: Analytics & BI (Yellow/Gold) */}
        {/* We use a solid glowing ring and a dashed spinning ring to simulate data streams */}
        <div className="absolute inset-0 border-[3px] border-yellow-400 border-dashed rounded-full animate-[spin_12s_linear_infinite] opacity-80 shadow-[0_0_15px_rgba(250,204,21,0.6)]"></div>
        <div className="absolute inset-0 border-2 border-yellow-200 rounded-full opacity-30 shadow-[inset_0_0_10px_rgba(250,204,21,0.4)]"></div>

        {/* Orbiting Planets / Apps */}
        {/* Planet 1: CRM (Blue) */}
        <div className="absolute inset-0 animate-[spin_3s_linear_infinite]">
          <div className="absolute top-0 left-1/2 -ml-2.5 -mt-2.5 w-5 h-5 rounded-full bg-[#5C93FA] shadow-[0_0_12px_rgba(92,147,250,0.8)] border border-white/40"></div>
        </div>
        
        {/* Planet 2: ERP (Green) */}
        <div className="absolute inset-0 animate-[spin_4.5s_linear_infinite]" style={{ animationDelay: '-1s' }}>
          <div className="absolute top-0 left-1/2 -ml-2.5 -mt-2.5 w-5 h-5 rounded-full bg-[#42B883] shadow-[0_0_12px_rgba(66,184,131,0.8)] border border-white/40"></div>
        </div>

        {/* Planet 3: HRMS (Purple) */}
        {/* Since HRMS is purple, and the core is purple, this fits perfectly! */}
        <div className="absolute inset-0 animate-[spin_3.8s_linear_infinite]" style={{ animationDelay: '-2.5s' }}>
          <div className="absolute top-0 left-1/2 -ml-2.5 -mt-2.5 w-5 h-5 rounded-full bg-[#9B6BFA] shadow-[0_0_12px_rgba(155,107,250,0.8)] border border-white/40"></div>
        </div>

        {/* Planet 4: LMS (Golden) */}
        <div className="absolute inset-0 animate-[spin_5.2s_linear_infinite]" style={{ animationDelay: '-0.5s' }}>
          <div className="absolute top-0 left-1/2 -ml-2.5 -mt-2.5 w-5 h-5 rounded-full bg-[#EFAD4D] shadow-[0_0_12px_rgba(239,173,77,0.8)] border border-white/40"></div>
        </div>

        {/* Planet 5: Finance (Reddish) */}
        <div className="absolute inset-0 animate-[spin_4.1s_linear_infinite]" style={{ animationDelay: '-3.2s' }}>
          <div className="absolute top-0 left-1/2 -ml-2.5 -mt-2.5 w-5 h-5 rounded-full bg-[#F46464] shadow-[0_0_12px_rgba(244,100,100,0.8)] border border-white/40"></div>
        </div>
      </div>

      <div className="flex flex-col items-center">
        <div className="flex space-x-2 text-3xl font-black tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-[#2AC3A2] via-blue-500 to-[#2C71E5] bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
          <span className="animate-pulse" style={{ animationDelay: '0ms' }}>O</span>
          <span className="animate-pulse" style={{ animationDelay: '150ms' }}>B</span>
          <span className="animate-pulse" style={{ animationDelay: '300ms' }}>I</span>
          <span className="animate-pulse" style={{ animationDelay: '450ms' }}>S</span>
        </div>
        <p className="mt-3 text-sm text-gray-500 font-bold tracking-[0.3em] uppercase">{text}</p>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes gradient {
          0% { background-position: 0% center; }
          100% { background-position: -200% center; }
        }
      `}} />
    </div>
  );
};

export const ObisSpinnerFullScreen: React.FC<{ text?: string }> = ({ text }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
    <ObisSpinner text={text} />
  </div>
);

export default ObisSpinner;
